import { useState, useEffect } from 'react';
import api from '../utils/api';

const WORK_AUTH_OPTIONS = ['US Citizen', 'Green Card', 'H1B', 'H4 EAD', 'L2 EAD', 'TN', 'OPT/CPT'];

const EMPTY = {
  first_name: '', middle_name: '', last_name: '', email: '', phone: '', address: '',
  contractor_subtype: 'corp_to_corp', work_authorization: '', work_auth_expiry: '',
  ssn_last4: '', date_of_birth: '', linkedin: '', notes: '',
  account_number: '', routing_number: '', bank_name: '', account_type: 'Checking',
};

export default function ContractorEditDrawer({ contractor, onClose, onSaved }) {
  const [form, setForm] = useState(() => contractor ? { ...EMPTY, ...contractor } : EMPTY);
  const [workAuthIsOther, setWorkAuthIsOther] = useState(() => {
    const existing = contractor?.work_authorization;
    return Boolean(existing) && !WORK_AUTH_OPTIONS.includes(existing);
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(contractor?.id);

  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(isEdit);
  const [newContact, setNewContact] = useState({ contact_type: 'email', value: '', label: '' });

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/contractors/${contractor.id}/contacts`)
      .then(r => setContacts(r.data))
      .finally(() => setLoadingContacts(false));
  }, [isEdit, contractor]);

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function addContact() {
    if (!newContact.value.trim()) return;
    try {
      const r = await api.post(`/contractors/${contractor.id}/contacts`, newContact);
      const refreshed = await api.get(`/contractors/${contractor.id}/contacts`); setContacts(refreshed.data);
      setNewContact({ contact_type: 'email', value: '', label: '' });
    } catch (e) {
      setError(e.response?.data?.error || 'Could not add contact');
    }
  }

  async function removeContact(contactId) {
    try {
      await api.delete(`/contractors/${contractor.id}/contacts/${contactId}`);
      setContacts(c => c.filter(x => x.id !== contactId));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not remove contact');
    }
  }

  async function makePrimary(contactId, contactType) {
    try {
      await api.put(`/contractors/${contractor.id}/contacts/${contactId}`, { is_primary: true });
      setContacts(c => c.map(x => x.contact_type === contactType ? { ...x, is_primary: x.id === contactId ? 1 : 0 } : x));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not update contact');
    }
  }

  async function handleSave() {
    if (!form.first_name || !form.last_name) {
      setError('First name and last name are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let contractorId = contractor?.id;
      if (isEdit) {
        await api.put(`/contractors/${contractor.id}`, form);
      } else {
        const r = await api.post('/contractors', form);
        contractorId = r.data.id;
      }

      if (newContact.value.trim()) {
        await api.post(`/contractors/${contractorId}/contacts`, newContact);
      }

      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save contractor');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="edit-drawer">
        <div className="drawer-header">
          <h3 className="drawer-title">{isEdit ? 'Edit Contractor' : 'Add Contractor'}</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        <div className="drawer-body">
          {error && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 18 }}>
              {error}
            </div>
          )}

          <div className="form-grid">
            <div className="form-field">
              <label>First name</label>
              <input value={form.first_name} onChange={e => set('first_name', e.target.value)} autoFocus />
            </div>
            <div className="form-field">
              <label>Last name</label>
              <input value={form.last_name} onChange={e => set('last_name', e.target.value)} />
            </div>

            <div className="form-field">
              <label>Middle name</label>
              <input value={form.middle_name || ''} onChange={e => set('middle_name', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Subtype</label>
              <select value={form.contractor_subtype} onChange={e => set('contractor_subtype', e.target.value)}>
                <option value="corp_to_corp">Corp-to-Corp</option>
                <option value="w2">W2</option>
                <option value="1099">1099</option>
              </select>
            </div>

            <div className="form-field">
              <label>Bank Name</label>
              <input value={form.bank_name || ''} onChange={e => set('bank_name', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Account Number</label>
              <input value={form.account_number || ''} onChange={e => set('account_number', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Routing Number</label>
              <input value={form.routing_number || ''} onChange={e => set('routing_number', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Account Type</label>
              <select value={form.account_type || 'Checking'} onChange={e => set('account_type', e.target.value)}>
                <option value="Checking">Checking</option>
                <option value="Savings">Savings</option>
              </select>
            </div>

            <div className="form-field span-2">
              <label>Address</label>
              <input value={form.address || ''} onChange={e => set('address', e.target.value)} />
            </div>

            <div className="form-field">
              <label>Work authorization</label>
              <select
                value={workAuthIsOther ? 'Other' : (form.work_authorization || '')}
                onChange={e => {
                  if (e.target.value === 'Other') {
                    setWorkAuthIsOther(true);
                    set('work_authorization', '');
                  } else {
                    setWorkAuthIsOther(false);
                    set('work_authorization', e.target.value);
                  }
                }}
              >
                <option value="">Select…</option>
                {WORK_AUTH_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                <option value="Other">Other (specify)</option>
              </select>
              {workAuthIsOther && (
                <input
                  style={{ marginTop: 8 }}
                  value={form.work_authorization || ''}
                  onChange={e => set('work_authorization', e.target.value)}
                  placeholder="Enter work authorization"
                  autoFocus
                />
              )}
            </div>
            <div className="form-field">
              <label>Work auth expiry</label>
              <input type="date" value={form.work_auth_expiry || ''} onChange={e => set('work_auth_expiry', e.target.value)} />
            </div>

            <div className="form-field">
              <label>SSN (last 4)</label>
              <input value={form.ssn_last4 || ''} onChange={e => set('ssn_last4', e.target.value)} maxLength={4} />
            </div>
            <div className="form-field">
              <label>Date of birth</label>
              <input type="date" value={form.date_of_birth || ''} onChange={e => set('date_of_birth', e.target.value)} />
            </div>

            <div className="form-field span-2">
              <label>LinkedIn</label>
              <input value={form.linkedin || ''} onChange={e => set('linkedin', e.target.value)} />
            </div>

            <div className="form-field span-2">
              <label>Notes</label>
              <textarea rows={3} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
            </div>
          </div>

          <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 4px' }}>Emails & phone numbers</h4>
            <p className="form-hint" style={{ marginBottom: 14 }}>
              A contractor can have more than one — for example a personal email and a vendor's official contact. Mark one of each type as primary; that's what shows in lists.
              {!isEdit && ' This first one will be saved automatically when you click "Add contractor" below.'}
            </p>

            {isEdit && (
              <>
                {loadingContacts && <div className="form-hint">Loading contacts…</div>}

                {!loadingContacts && contacts.length === 0 && (
                  <div className="form-hint">No additional contacts yet — the email/phone above will be used.</div>
                )}

                {!loadingContacts && contacts.map(c => (
                  <div key={c.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0',
                    borderBottom: '1px solid var(--border)'
                  }}>
                    <span className="badge badge-neutral" style={{ minWidth: 50, justifyContent: 'center' }}>
                      {c.contact_type === 'email' ? 'Email' : 'Phone'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14 }}>{c.value}</div>
                      {c.label && <div className="form-hint">{c.label}</div>}
                    </div>
                    {c.is_primary
                      ? <span className="badge badge-success">Primary</span>
                      : <button className="btn btn-ghost" onClick={() => makePrimary(c.id, c.contact_type)}>Make primary</button>}
                    <button className="drawer-close" onClick={() => removeContact(c.id)} title="Remove">×</button>
                  </div>
                ))}
              </>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'flex-end' }}>
              <div className="form-field" style={{ width: 90 }}>
                <label>Type</label>
                <select value={newContact.contact_type} onChange={e => setNewContact(c => ({ ...c, contact_type: e.target.value }))}>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                </select>
              </div>
              <div className="form-field" style={{ flex: 1 }}>
                <label>Value</label>
                <input
                  value={newContact.value}
                  onChange={e => setNewContact(c => ({ ...c, value: e.target.value }))}
                  placeholder={newContact.contact_type === 'email' ? 'name@example.com' : '555-123-4567'}
                />
              </div>
              <div className="form-field" style={{ flex: 1 }}>
                <label>Label</label>
                <input
                  value={newContact.label}
                  onChange={e => setNewContact(c => ({ ...c, label: e.target.value }))}
                  placeholder="e.g. Personal, Vendor Contact"
                />
              </div>
              {isEdit && (
                <button className="btn btn-secondary" onClick={addContact}>+ Add now</button>
              )}
            </div>
            {isEdit && newContact.value.trim() && (
              <p className="form-hint" style={{ marginTop: 6 }}>
                This will also be saved automatically when you click "Save changes" below — "+ Add now" only saves it immediately, in case you want to add several before closing.
              </p>
            )}
          </div>
        </div>

        <div className="drawer-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add contractor'}
          </button>
        </div>
      </div>
    </>
  );
}
